import { format } from 'date-fns';
import { Calendar, Camera, Check, Circle, CircleCheck, Clock, HardDrive, Image, Volume2 } from 'lucide-react';
import { Time } from '@/components/ui/time';
import { MonitorAlertEventDto, MonitorAlertFullDto} from '@/app/Alert/types';
import { getDeviceNameByNvrId, parseDateWithoutTimezone } from '../utils';
import { useContextualTimeZone } from '@/components/TimezoneProvider';
import { formatInTimeZone } from 'date-fns-tz';
import { useAdminAlertContext } from './AdminAlertProvider';
import { useCallback } from 'react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { FeatureFlags } from '@/lib/feature-flag';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addAlertNote } from '@/lib/api/monitoring/alert';
import { useAuthStore } from '@/store/authStore';
import { useToast } from '@/hooks/use-toast';
import { createUntagNoteText, formatEventTimeForTag } from '@/lib/tagging';

export function AdminAlertEventList() {
  const { timeZone } = useContextualTimeZone();
  const taggingEnabled = useFeatureFlagEnabled(FeatureFlags.Tagging);
  const { 
    alert, 
    selectedEvent, 
    setSelectedEvent, 
    sortedEventsForNavigation,
    groupedEvents,
    eventsContainerRef,
    eventRefs,
    setSelectedEventImage,
    setViewIndex,
    getEventThumbnail,
    eventsData,
    taggedEventIds,
    setShowTagDialog,
    startTimeOverrideMs,
    setStartTimeOverrideMs,
    imageCache,
  } = useAdminAlertContext();

  const user = useAuthStore((state) => state.user);
  const userFullName = user ? `${user.firstName} ${user.lastName}`.trim() : 'Agent';
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const removeTagMutation = useMutation({
    mutationFn: async (event: MonitorAlertEventDto) => {
      if (!alert?.id || !alert?.companyId || !alert?.projectId || !event.id) {
        throw new Error('Missing alert data');
      }
      const eventTime = formatEventTimeForTag(event.occurred, timeZone);
      return addAlertNote({
        alertId: alert.id,
        companyId: alert.companyId,
        projectId: alert.projectId,
        note: createUntagNoteText({ eventId: event.id, eventTime }),
        author: userFullName,
      });
    },
    onSuccess: () => {
      if (alert?.id) {
        queryClient.invalidateQueries({ queryKey: ['fullAlert', alert.id] });
        queryClient.invalidateQueries({ queryKey: ['alert', alert.id] });
      }
      toast({ title: 'Tag Removed', duration: 3000 });
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Failed to remove tag', duration: 3000 });
    },
  });

  const handleTagClick = (event: MonitorAlertEventDto, e: React.MouseEvent) => {
    e.stopPropagation(); // Don't trigger the row's onClick
    // Select the event first
    setSelectedEvent(event);
    setSelectedEventImage(getEventThumbnail(event) || null);
    setViewIndex(sortedEventsForNavigation.findIndex((ev) => ev.id === event.id));

    if (event.id && taggedEventIds.has(event.id)) {
      removeTagMutation.mutate(event);
    } else {
      setShowTagDialog(true);
    }
  };

  const handleSetStartTime = async (event: MonitorAlertEventDto) => {
    if (!event.occurred || !alert?.id || !alert?.companyId || !alert?.projectId) return;
    const eventMs = new Date(event.occurred).getTime();
    const isAlreadySet = startTimeOverrideMs !== null && eventMs === startTimeOverrideMs;

    setStartTimeOverrideMs(isAlreadySet ? null : eventMs);
    const overrideNote = isAlreadySet
      ? '[START_TIME_OVERRIDE]NONE'
      : `[START_TIME_OVERRIDE]${new Date(event.occurred).toISOString()}`;
    try {
      await addAlertNote({
        alertId: alert.id,
        companyId: alert.companyId,
        projectId: alert.projectId,
        note: overrideNote,
        author: userFullName,
      });
      queryClient.invalidateQueries({ queryKey: ['fullAlert', alert.id] });
      queryClient.invalidateQueries({ queryKey: ['alert', alert.id] });
      toast({
        title: isAlreadySet ? 'Start time removed' : 'Start time updated',
        description: isAlreadySet ? undefined : 'Incident start time has been set to this event.',
        duration: 3000,
      });
    } catch {
      setStartTimeOverrideMs(isAlreadySet ? startTimeOverrideMs : null);
      toast({ variant: 'destructive', title: `Failed to ${isAlreadySet ? 'remove' : 'set'} start time`, duration: 3000 });
    }
  };

  // Helper function to get event type styling and human-readable text
    const getEventTypeInfo = useCallback((eventType: string) => {
      const eventTypeMap: Record<string, { text: string; bgColor: string; dotColor: string }> = {
        Unknown: { text: 'Unknown', bgColor: 'bg-gray-500/20', dotColor: 'bg-gray-500' },
        CameraOnline: { text: 'Camera Online', bgColor: 'bg-green-500/20', dotColor: 'bg-green-500' },
        CameraOffline: { text: 'Camera Offline', bgColor: 'bg-red-500/20', dotColor: 'bg-red-500' },
        CameraTampered: { text: 'Camera Tampered', bgColor: 'bg-red-600/20', dotColor: 'bg-red-600' },
        CameraFailRecord: {
          text: 'Camera Fail Record',
          bgColor: 'bg-red-500/20',
          dotColor: 'bg-red-500',
        },
        CameraOverheated: {
          text: 'Camera Overheated',
          bgColor: 'bg-orange-500/20',
          dotColor: 'bg-orange-500',
        },
        CameraMotion: { text: 'Camera Motion', bgColor: 'bg-blue-500/20', dotColor: 'bg-blue-500' },
        PersonSeen: { text: 'Person Seen', bgColor: 'bg-yellow-500/20', dotColor: 'bg-yellow-500' },
        PersonLoitering: {
          text: 'Person Loitering',
          bgColor: 'bg-orange-600/20',
          dotColor: 'bg-orange-600',
        },
        PersonCount: { text: 'Person Count', bgColor: 'bg-purple-500/20', dotColor: 'bg-purple-500' },
        PersonWallScale: {
          text: 'Person Wall Scale',
          bgColor: 'bg-pink-500/20',
          dotColor: 'bg-pink-500',
        },
        VehicleSeen: { text: 'Vehicle Seen', bgColor: 'bg-cyan-500/20', dotColor: 'bg-cyan-500' },
        Analytic: { text: 'Analytic', bgColor: 'bg-indigo-500/20', dotColor: 'bg-indigo-500' },
        SoundDetected: { text: 'Sound Detected', bgColor: 'bg-violet-500/20', dotColor: 'bg-violet-500' },
      };
  
      return (
        eventTypeMap[eventType] || {
          text: eventType,
          bgColor: 'bg-gray-500/20',
          dotColor: 'bg-gray-500',
        }
      );
    }, []);
  return (
    <div
      ref={eventsContainerRef}
      data-onboarding="admin-alert-events"
      className="overflow-y-scroll border-r"
    > <div className="text-md font-semibold h-[56px] flex items-center px-4 py-3 sticky top-0 border-b bg-[#1b1b1b] z-20 gap-2">
<Image className="h-6 w-6" /> {eventsData.length}  available events
  </div>
      {Object.keys(groupedEvents)
        .sort((a, b) => {
          if (a === 'Unknown') return 1;
          if (b === 'Unknown') return -1;
          return new Date(a).getTime() - new Date(b).getTime();
        })
        .map((dateKey) => (
          <div key={dateKey}>
            <div className="sticky top-0 z-10 flex h-[56px] items-center gap-2 border-b bg-[#1b1b1b] px-2 font-semibold text-white text-sm">
   
              {dateKey === 'Unknown' ? 'Unknown Date' : format(parseDateWithoutTimezone(dateKey), 'dd MMMM yyyy')}
            </div>
            {groupedEvents[dateKey].map((event) => (
              <div
                key={event.id}
                ref={(el) => {
                  if (el && event.id) {
                    eventRefs.current.set(event.id, el);
                  } else if (event.id) {
                    eventRefs.current.delete(event.id);
                  }
                }}
                onClick={() => {
                  setSelectedEvent(event);
                  setSelectedEventImage(getEventThumbnail(event) || null);
                  setViewIndex(sortedEventsForNavigation.findIndex((e) => e.id === event.id));
                }}
                className={`cursor-pointer border-l-4 border-transparent hover:bg-gray-800 ${
                  selectedEvent?.id === event.id ? 'border-l-lime-500 bg-gray-700' : ''
                }`}
              >
                <div className="flex items-center gap-2 border-b text-sm text-gray-300">
                  <div className="h-12 w-24 overflow-hidden rounded border bg-gray-800">
                    {event.id && imageCache[event.id] ? (
                      <img
                        src={imageCache[event.id]}
                        alt="Event thumbnail"
                        className="object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-gray-500">
                        No Image
                      </div>
                    )}
                  </div>
                  <div className="w-full">
                    <div className="mb-0.5 flex items-center justify-between gap-2">
                      <div >
                       
                        <p className='text-xs font-semibold'><Time date={event.occurred} format="h:mm:ss a" fallback="N/A" noTooltip /></p>
                        <p className='text-xs text-gray-400'>{getEventTypeInfo(event.type || 'Unknown').text} </p>
                      </div>
                      
                 {taggingEnabled && (
                   <div className="flex items-center">
                     {(() => {
                       const isStartTimeEvent = !!(startTimeOverrideMs !== null && event.occurred && new Date(event.occurred).getTime() === startTimeOverrideMs);
                       return (
                         <div
                           className={`flex items-center justify-center p-2 rounded-md cursor-pointer ${isStartTimeEvent ? 'bg-green-500/10' : 'hover:bg-green-500/10'}`}
                           onClick={(e) => {
                             e.stopPropagation();
                             handleSetStartTime(event);
                           }}
                           title="Set as incident start time"
                         >
                           <Clock className={`h-4 w-4 ${isStartTimeEvent ? 'text-lime' : 'text-gray-600/40 '}`} />
                         </div>
                       );
                     })()}
                     <div
                       className='flex items-center justify-center p-2 hover:bg-lime/10 rounded-md cursor-pointer'
                       onClick={(e) => handleTagClick(event, e)}
                     >
                       {event.id && taggedEventIds.has(event.id) ? (
                         <CircleCheck className="h-5 w-5 text-lime/70" />
                       ) : (
                         <Circle className="h-5 w-5 text-gray-600/40" />
                       )}
                     </div>
                   </div>
                 )}
                    </div>
                   
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}

     
 
    </div>
  );
}
